import * as d3 from 'd3'
import cloneDeep from 'lodash/cloneDeep'
import { format, differenceInYears } from 'date-fns'
import { formatAbbrCurrency } from '../helpers/formatters'
import linkSrc from '../assets/homes/img/tune-your-value-icon.svg'
import { AVM_TUNE_TYPES, AVM_SOURCE_TYPES } from '../constants/valuation'

class ValueLineChart {
  constructor(el, theme) {
    this.el = el
    this._buildSvg()
    this._setupResize()
    this.theme = theme
  }

  update(data) {
    this.data = data ? this._prepData(data) : this.data
    this._combineValuations()
    this._draw()
    if (data.disableValue) {
      this.tip.attr('class', 'hidden')
    }
  }

  destroy() {
    // ensure all event listeners are cleaned up
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this._redrawChart)
      this.deviceOrientation.removeEventListener?.('change', this._redrawChart)
    }
  }

  _draw() {
    this.svg.selectAll('g').remove()
    this.svg.selectAll('defs').remove()
    this._buildChartContainer()
    this._calcDimensions()
    this._shiftChartContainer()
    this._calcScales()
    this._axes()
    this._addPhotoCropperBelowLineGradientAndLineGlow()
    this._segmentConnectors()
    this._segments()
  }

  _buildSvg() {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer() {
    this.chart = this.svg.append('g')
  }

  _shiftChartContainer() {
    this.chart.attr('transform', `translate(35,${this.margin.top})`)
  }

  _axes() {
    this._drawXAxis()
    this._drawYAxis()
  }

  _segments() {
    const line = d3
      .line()
      .curve(d3.curveCardinal)
      .x(d => this.scaleX(d.date))
      .y((d, i) => this.scaleY(d.valuation))

    this.segment = this.chart
      .selectAll('.segment')
      .data(this.data.segments)
      .enter()
      .append('g')
      .attr('class', 'segment')

    this.segment
      .filter(d => d.valuations.length > 1)
      .append('path')
      .attr('class', 'line')
      .attr('d', d => line(d.valuations))

    this.segment
      .filter(d => d.valuations.length > 1)
      .append('path')
      .attr('class', 'line')
      .attr('d', d => line(d.valuations))
      .style('filter', 'url(#glow)')
      .style('opacity', '.4')

    const area = d3
      .area()
      .curve(d3.curveCardinal)
      .x(dataPoint => this.scaleX(dataPoint.date))
      .y0(this.chartHeight)
      .y1(dataPoint => this.scaleY(dataPoint.valuation))

    this.segment.append('path').datum(this.allValuations).style('fill', 'url(#gradient)').attr('d', area)

    this.segment
      .selectAll('g')
      .data(d => d.valuations)
      .enter()
      .append('g')
      .classed('datapoint', true)
      .call(parentDatapoint => {
        this._addDataPoints(parentDatapoint)
      })

    this._setupTips()
  }

  _segmentConnectors() {
    const line = d3
      .line()
      .x(d => this.scaleX(d.date))
      .y((d, i) => this.scaleY(d.valuation))

    this.connector = this.chart
      .selectAll('.line--connector')
      .data(this.data.connectors.filter(d => d.valuations.every(Boolean)))
      .enter()

    this.connector
      .append('path')
      .attr('class', 'line line--connector')
      .attr('d', d => line(d.valuations))

    this.connector
      .append('path')
      .attr('class', 'line line--connector')
      .attr('d', d => line(d.valuations))
      .style('filter', 'url(#glow)')
      .style('opacity', '.4')
  }

  _addDataPoints(datapoint) {
    this._photoPosts(datapoint)
    this._points(datapoint)
    this._photoBorders(datapoint)
    this._photos(datapoint)
    this._wrenchFills(datapoint)
    this._wrenches(datapoint)
  }

  _photoPosts(datapoint) {
    datapoint
      .filter(
        (d, i) =>
          i === 0 &&
          d.source &&
          Object.values(AVM_TUNE_TYPES).includes(d.type) &&
          (d.source.type === AVM_SOURCE_TYPES.CLIENT || d.source.photoUrl)
      )
      .append('line')
      .classed('photo-post', true)
      .attr('x1', d => this.scaleX(d.date))
      .attr('y1', d => this.scaleY(d.valuation))
      .attr('x2', d => this.scaleX(d.date))
      .attr('y2', d => this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation))
  }

  _points(datapoint) {
    datapoint
      .append('circle')
      .classed('point', true)
      .attr('cx', d => this.scaleX(d.date))
      .attr('cy', d => this.scaleY(d.valuation))
      .attr('r', 1.5)
  }

  _photoBorders(datapoint) {
    datapoint
      .filter((d, i) => i === 0 && d.source && Object.values(AVM_TUNE_TYPES).includes(d.type) && d.source.photoUrl)
      .append('circle')
      .classed('photo-border', true)
      .attr('r', this._getPhotoWidth() / 2)
      .attr('transform', d => {
        const x = this.scaleX(d.date)
        const y = this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'middle')
        return `translate(${x},${y})`
      })
  }

  _photos(datapoint) {
    datapoint
      .filter((d, i, a) => i === 0 && d.source && Object.values(AVM_TUNE_TYPES).includes(d.type) && d.source.photoUrl)
      .append('image')
      .classed('photo', true)
      .attr('xlink:href', d => d.source.photoUrl)
      .attr('width', this._getPhotoWidth())
      .attr('height', this._getPhotoWidth())
      .attr('transform', d => {
        const x = this.scaleX(d.date) - this._getPhotoWidth() / 2
        const y = this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'top')
        return `translate(${x},${y})`
      })
      .attr('preserveAspectRatio', 'xMaxYMin slice')
      .style('clip-path', `url(#photoCircleCrop)`) // http://stackoverflow.com/a/18265336/580366
  }

  _wrenchFills(datapoint) {
    datapoint
      .filter(
        (d, i) => i === 0 && d.source && d.type === AVM_TUNE_TYPES.TYV && d.source.type === AVM_SOURCE_TYPES.CLIENT
      )
      .append('circle')
      .classed('wrench-fill', true)
      .attr('r', this._getPhotoWidth() / 2)
      .attr('transform', d => {
        const x = this.scaleX(d.date)
        const y = this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'middle')
        return `translate(${x},${y})`
      })
  }

  _wrenches(datapoint) {
    const urlBase = window.location.href

    datapoint
      .filter((d, i, a) => i === 0 && d.source && d.type === AVM_TUNE_TYPES.TYV && d.source.type === 'client')
      .append('image')
      .classed('icon', true)
      .attr('width', this._getPhotoWidth() / 2)
      .attr('height', this._getPhotoWidth() / 2)
      .attr('xlink:href', linkSrc)
      .attr('transform', d => {
        const x = this.scaleX(d.date) - this._getPhotoWidth() / 4
        const y = this.scaleY(d.valuation) + this._getPhotoOffset(d.valuation, 'top') + this._getPhotoWidth() / 4
        return `translate(${x},${y})`
      })
      .attr('preserveAspectRatio', 'xMaxYMin slice')
      .style('clip-path', `url(${urlBase}#photoCircleCrop)`) // http://stackoverflow.com/a/18265336/580366
  }

  _drawXAxis() {
    const years = differenceInYears(this.xDomain[1], this.xDomain[0])
    const xAxis = d3.axisBottom(this.scaleX).tickFormat(d3.timeFormat(years >= 4 ? '%Y' : '%b %Y'))

    if (years >= 4) {
      xAxis.ticks(years)

      return this.chart
        .append('g')
        .attr('class', 'axis axis--x')
        .attr('transform', `translate(0,${this.chartHeight - this.margin.bottom / 2 + 5})`)
        .call(xAxis)
    } else if (this.allValuations.length > 4) {
      xAxis
        .ticks(5)
        .tickValues([
          this.allValuations[0].date,
          this.allValuations[Math.floor(this.allValuations.length * 0.25)].date,
          this.allValuations[Math.floor(this.allValuations.length * 0.5)].date,
          this.allValuations[Math.floor(this.allValuations.length * 0.75)].date,
          this.allValuations[this.allValuations.length - 1].date
        ])
    } else {
      xAxis.ticks(this.allValuations.length)
    }

    return this.chart
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', `translate(0,${this.chartHeight - this.margin.bottom / 2 + 5})`)
      .call(xAxis)
      .selectAll('.tick text')
      .attr('class', 'custom-tick')
      .call(this._wrapAxisLabels, 10)
  }

  _wrapAxisLabels(text, width) {
    text.each(function () {
      var text = d3.select(this),
        words = text.text().split(/\s+/).reverse(),
        word,
        line = [],
        lineNumber = 0,
        lineHeight = 1.1, // ems
        y = text.attr('y'),
        dy = parseFloat(text.attr('dy')),
        tspan = text
          .text(null)
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', dy + 'em')
      while ((word = words.pop())) {
        line.push(word)
        tspan.text(line.join(' '))
        if (tspan.node().getComputedTextLength() > width) {
          line.pop()
          tspan.text(line.join(' '))
          line = [word]
          tspan = text
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + dy + 'em')
            .text(word)
        }
      }
    })
  }

  _drawYAxis() {
    if (this.data.disableValue) return
    const yAxis = d3
      .axisRight(this.scaleY)
      .tickSize(this.outerWidth - 40)
      .tickArguments([6])
      .tickFormat(d3.format('.2s'))

    // Add to outer svg because it's outside the graph on the left
    // Prepend instead of append so that it's under the chart
    this.yAxis = this.svg
      .insert('g', ':first-child')
      .attr('class', 'axis axis--y')
      .attr('transform', `translate(0, ${this.margin.top})`)
      .call(yAxis)

    this.yAxis.selectAll('text').attr('x', 0).attr('dy', -6)
    this.yAxis
      .selectAll('line')
      .style('transform', 'translateX(35px)')
      .style(
        'stroke',
        `var(${this.theme.isLightThemeType ? '--hb-colors-blackAlpha-100' : '--hb-colors-whiteAlpha-100'})`
      )
    this.yAxis
      .select('line')
      .style(
        'stroke',
        `var(${this.theme.isLightThemeType ? '--hb-colors-blackAlpha-300' : '--hb-colors-whiteAlpha-300'})`
      )
  }

  _addPhotoCropperBelowLineGradientAndLineGlow() {
    const halfPhotoWidth = this._getPhotoWidth() / 2
    const defs = this.svg.append('defs')

    defs
      .append('clipPath')
      .attr('id', 'photoCircleCrop')
      .append('circle')
      .attr('r', halfPhotoWidth)
      .attr('cx', halfPhotoWidth)
      .attr('cy', halfPhotoWidth)

    const gradient = defs
      .append('linearGradient')
      .attr('id', 'gradient')
      .attr('x1', '0%')
      .attr('y1', '100%')
      .attr('x2', '0%')
      .attr('y2', '0%')

    gradient
      .append('stop')
      .attr('offset', '10%')
      .attr('style', 'stop-color:var(--brand-primary-custom, var(--brand-primary-600));stop-opacity:0.001')

    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:var(--brand-primary-custom, var(--brand-primary-600));stop-opacity:.25')

    var graphLineGlow = defs.append('filter').attr('id', 'glow').attr('filterUnits', 'userSpaceOnUse')
    graphLineGlow.append('feGaussianBlur').attr('stdDeviation', '5').attr('result', 'coloredBlur')
    var feMerge = graphLineGlow.append('feMerge')
    feMerge.append('feMergeNode').attr('in', 'coloredBlur')
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic')
  }

  _setupTips() {
    const tooltip = d3.select('#tooltip').style('display', 'none')
    const tooltipDot = this.chart
      .append('circle')
      .attr('r', 5)
      .attr('fill', 'var(--brand-primary-custom, var(--brand-primary-600))')
      .style(
        'stroke',
        `${this.theme.isLightThemeType ? 'var(--hb-colors-blackAlpha-200)' : 'var(--hb-colors-whiteAlpha-200)'}`
      )
      .style('stroke-width', '2')
      .style('opacity', 0)
      .style('pointer-events', 'none')
    this.chart
      .append('rect')
      .attr('width', this.chartWidth)
      .attr('height', this.chartHeight)
      .style('opacity', 0)
      .on('mouseleave', () => {
        tooltip.style('display', 'none')
        tooltipDot.style('opacity', '0')
      })
      .on('mousemove', data => {
        const coordinates = d3.pointer(data)
        const hoveredDate = this.scaleX.invert(coordinates[0])
        const distanceFromHoveredDate = d => Math.abs(d.date - hoveredDate)

        const closestIndex = d3.scan(
          this.allValuations,
          (a, b) => distanceFromHoveredDate(a) - distanceFromHoveredDate(b)
        )

        const hoveredIndexData = this.allValuations[closestIndex]

        tooltipDot
          .style('opacity', 1)
          .attr('cx', this.scaleX(hoveredIndexData.date))
          .attr('cy', this.scaleY(hoveredIndexData.valuation))
          .raise()

        tooltip
          .style('display', 'block')
          .style('pointer-events', 'none')
          .style('top', `${this.scaleY(hoveredIndexData.valuation)}px`)
          .style('left', `${this.scaleX(hoveredIndexData.date)}px`)
          .style('transform', 'translateY(-5.75rem)')

        tooltip.select('.avm').text(`${formatAbbrCurrency(hoveredIndexData.valuation)}`)

        const dateFormatter = d3.timeFormat('%b %Y')

        tooltip.select('.date').text(`${dateFormatter(hoveredIndexData.date)}`)
      })
  }

  _calcDimensions() {
    this.margin = { top: 0, right: 20, bottom: 30, left: 20 }
    this.outerWidth = this.el.clientWidth
    this.outerHeight = this.el.clientHeight
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales() {
    this.scaleX = d3.scaleTime().domain(this.xDomain).range([0, this.chartWidth])
    this.scaleY = d3.scaleLinear().domain(this._getPaddedYDomain()).range([this.chartHeight, 0])
  }

  _combineValuations() {
    const data = cloneDeep(this.data)
    let valuations = []
    data.segments.forEach(segment => (valuations = valuations.concat(segment.valuations)))
    this.allValuations = valuations
  }

  _prepData(data) {
    let transformedData = cloneDeep(data)
    transformedData = this._setupSourceAndDates(transformedData)
    transformedData.connectors = this._connectorData(transformedData)

    return transformedData
  }

  _setupSourceAndDates(data) {
    const transformedData = cloneDeep(data)
    const parseTime = d3.timeParse('%Y-%m')

    transformedData.segments.forEach((segment, i) => {
      segment.valuations = segment.valuations.map(valuation => {
        valuation.source = segment.source
        valuation.date = parseTime(
          format(
            new Date(valuation.date.toString().replaceAll('-', '/')), // This ensures we get the correct date when formatting (crazy JS date nuances): https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
            'yyyy-MM'
          )
        )
        return valuation
      })
    })

    return transformedData
  }

  _connectorData(data) {
    const connectors = []

    data.segments.forEach((segment, i) => {
      if (i > 0) {
        const connector = {}
        const previousSegment = data.segments[i - 1]
        connector.valuations = [
          previousSegment.valuations[previousSegment.valuations.length - 1],
          segment.valuations[0]
        ]
        connectors.push(connector)
      }
    })

    return connectors
  }

  get xDomain() {
    return d3.extent(this.allValuations, d => d.date)
  }

  _getPaddedYDomain() {
    let min = d3.min(this.allValuations, d => d.valuation)
    let max = d3.max(this.allValuations, d => d.valuation)

    // give the chart some breathing room, make it less dramatic
    min -= min * 0.5
    max += max * 0.15

    return [min, max]
  }

  _getPhotoOffset(value, origin) {
    const PHOTO_WIDTH = this._getPhotoWidth()
    let offset = PHOTO_WIDTH * 1.333

    if (origin === 'middle') offset += PHOTO_WIDTH / 2

    if (this.scaleY(value) > this.chartHeight / 2) {
      if (origin === 'top') offset += PHOTO_WIDTH

      return -offset
    }

    return offset
  }

  _getPhotoWidth() {
    return 24
  }

  _setupResize() {
    if (this._redrawChart) return

    this._redrawChart = () => {
      this._draw()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this._redrawChart)

      this.deviceOrientation = window.matchMedia('(orientation: portrait)')
      this.deviceOrientation.addEventListener?.('change', this._redrawChart)
    }
  }
}

export default ValueLineChart
